import React, { Component } from 'react';

import { differenceWith, intersectionWith, isEqual } from 'lodash';
import DividerLine from '../DividerLine';
import { ClearButton, MultipleRefined, RefineTitle } from './refine_components';
import { generateFiltersModel, FilterTypes, SeasonData } from '../../common/models';
import { SizeData } from '../../common/models/SizeData';
import { Brand, FilterSettings } from '../../common/models/FilterSettings';

class MultipleRefinedModel {
  data: any;

  text: any;

  constructor(data: any, text: any) {
    this.data = data;
    this.text = text;
  }
}

interface RefineBySectionProps {
  availableFilters: string[];
  department: string;
  filterSettings: FilterSettings;
  handleBrandsFilter: (brandsIds: string[]) => void;
  handleClearFilters: (param: any) => void;
  handleSeasonsFilter: (param: any) => void;
  handleSizesFilter: (param: any) => void;
  isDesktop: boolean;
  s3BasePath: string;
}

class RefineBySection extends Component<RefineBySectionProps, {}> {
  constructor(props: RefineBySectionProps) {
    super(props);
    this.filterBrands = this.filterBrands.bind(this);
    this.filterSeasons = this.filterSeasons.bind(this);
    this.filterSizes = this.filterSizes.bind(this);
    this.hasFilters = this.hasFilters.bind(this);
  }

  filterBrands(brand: Brand) {
    const available = differenceWith<string, string>(this.props.filterSettings.brandsIds, [brand.id], isEqual);
    this.props.handleBrandsFilter(available);
  }

  filterSeasons(ids) {
    const available = differenceWith(this.props.filterSettings.seasons, ids, isEqual);
    this.props.handleSeasonsFilter(available);
  }

  filterSizes(ids) {
    const available = differenceWith(this.props.filterSettings.sizes, ids, isEqual);
    this.props.handleSizesFilter(available);
  }

  hasFilters() {
    const { availableFilters, filterSettings } = this.props;
    const { brandsIds, sizes, seasons } = filterSettings;

    let hasFilters = false;

    if (availableFilters.includes(FilterTypes.designer())) {
      hasFilters = hasFilters || brandsIds.length > 0;
    }

    if (availableFilters.includes(FilterTypes.size())) {
      hasFilters = hasFilters || sizes.length > 0;
    }

    if (availableFilters.includes(FilterTypes.season())) {
      hasFilters = hasFilters || seasons.length > 0;
    }

    return hasFilters;
  }

  renderDesignerFilterIfNeeded() {
    const { availableFilters, filterSettings, isDesktop } = this.props;
    const { availableBrands, brandsIds } = filterSettings;

    const brandsData = availableBrands
      .filter((el: Brand) => intersectionWith([el.id], brandsIds, isEqual).length !== 0)
      .map((el: Brand) => new MultipleRefinedModel(el, el.name));

    return (
      availableFilters.includes(FilterTypes.designer()) && (
        <MultipleRefined
          bold
          filterList={brandsData}
          fontSize={13}
          ids={brandsIds}
          isDesktop={isDesktop}
          removeFilters={data => this.filterBrands(data)}
          title="designer"
        />
      )
    );
  }

  renderSeasonFilterIfNeeded() {
    const { availableFilters, filterSettings, isDesktop } = this.props;
    const { availableSeasons, currentSeason, seasons } = filterSettings;

    const allSeasonsData = generateFiltersModel(
      SeasonData.fromSeasonString(currentSeason),
      availableSeasons.map(el => SeasonData.fromSeasonString(el)),
      seasons.map(el => SeasonData.fromSeasonString(el))
    );

    const seasonsData = allSeasonsData
      .map(seasonData => new MultipleRefinedModel(seasonData.data.map(el => el.toString()), seasonData.text))
      .filter(seasonData => intersectionWith(seasons, seasonData.data, isEqual).length !== 0);

    return (
      availableFilters.includes(FilterTypes.season()) && (
        <MultipleRefined
          bold
          filterList={seasonsData}
          fontSize={13}
          ids={seasons}
          isDesktop={isDesktop}
          removeFilters={data => this.filterSeasons(data)}
          title="season"
        />
      )
    );
  }

  renderSizeFilterIfNeeded() {
    const { availableFilters, filterSettings, isDesktop, department } = this.props;
    const { sizes } = filterSettings;

    const existingSizes = SizeData.all(department);
    const sortedSizes = intersectionWith(
      existingSizes,
      sizes,
      (arrVal: SizeData, otherVal: SizeData) => arrVal.toStringRepresentation() === otherVal
    );

    const sizesData = sortedSizes.map(
      (el: SizeData) => new MultipleRefinedModel([el.toStringRepresentation()], el.toLongDisplayString())
    );

    return (
      availableFilters.includes(FilterTypes.size()) && (
        <MultipleRefined
          bold
          filterList={sizesData}
          fontSize={13}
          ids={sizes}
          isDesktop={isDesktop}
          removeFilters={(data: any) => this.filterSizes(data)}
          title="size"
        />
      )
    );
  }

  render() {
    const { availableFilters, isDesktop, handleClearFilters } = this.props;

    const style = isDesktop ? { marginBottom: '15px' } : {};

    return this.hasFilters() ? (
      <div data-cy="refine-by" style={style}>
        <RefineTitle bold isDesktop={isDesktop} padding={isDesktop ? '0' : '10px 5px 10px 5px'} title="Refined by" />
        <DividerLine padding="0 0 10px 0" size={1} />
        {this.renderDesignerFilterIfNeeded()}
        {this.renderSeasonFilterIfNeeded()}
        {this.renderSizeFilterIfNeeded()}
        <ClearButton handleClearFilters={() => handleClearFilters(availableFilters)} isDesktop={isDesktop} />
      </div>
    ) : null;
  }
}

export default RefineBySection;
